import { useCallback } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import LogbookSignersForm from "src/components/pages/Settings/AdvancedFeatures/LogbookSignersForm/LogbookSignersForm";
import SignersSectionHeader from "src/components/pages/Settings/AdvancedFeatures/SignersSectionHeader/SignersSectionHeader";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, usePostApi } from "src/hooks";
import { type ILink } from "src/interfaces";
import { PageContainer, SectionDivider } from "src/styled";
import { Notification } from "src/utils/Notification";

import {
  type ILogbookSignersForm,
  type ILogbookSignersData,
} from "./LogbookSigners.types";
import {
  emptySignerRow,
  mapGetSigners,
  rowsToSavePayload,
} from "./LogbookSigners.utils";

const LogbookSigners = () => {
  const { data, isLoading: isGetLoading } = useGetApi<ILogbookSignersData>({
    request: "get-signers",
    mapper: mapGetSigners,
  });

  const { post, isLoading: isPostLoading } = usePostApi();

  const methods = useForm<ILogbookSignersForm>({
    values: {
      rows: data?.rows?.length ? data.rows : [],
    },
  });

  const { append, ...fieldArrayProps } = useFieldArray<
    ILogbookSignersForm,
    "rows",
    "fieldId"
  >({
    control: methods.control,
    keyName: "fieldId",
    name: "rows",
  });

  const getLinks = useCallback((links: ILink[]) => {
    return links.map((link, index) =>
      index === links.length - 1 ? { ...link, title: "Logbook Signers" } : link,
    );
  }, []);

  const handleAppend = () => {
    append(emptySignerRow());
  };

  const handleSave = methods.handleSubmit(async formData => {
    const invalidRow = formData.rows.find(
      row => !row.name?.trim() || !row.lbid,
    );
    if (invalidRow) {
      Notification(
        "Validation Error",
        "Each row requires a name and logbook.",
        "red",
      );
      return;
    }

    const namesByLbid = new Map<string, Set<string>>();
    for (const row of formData.rows) {
      const key = row.lbid;
      const names = namesByLbid.get(key) ?? new Set();
      const trimmed = row.name.trim();
      if (names.has(trimmed)) {
        Notification(
          "Validation Error",
          `Duplicate name "${trimmed}" for the same logbook.`,
          "red",
        );
        return;
      }
      names.add(trimmed);
      namesByLbid.set(key, names);
    }

    await post({
      request: "save-signers",
      body: {
        params: rowsToSavePayload(formData.rows),
      },
      onSuccess: () => {
        Notification(
          "Success",
          "Logbook signers have been successfully saved.",
          "green",
        );
      },
    });
  });

  const handleRemove = async (index: number, id?: number) => {
    if (!id) return;
    await post({
      request: "remove-signer",
      body: {
        params: {
          id,
        },
      },
      onSuccess: () => {
        Notification("Success", "Signer configuration removed.", "green");
        fieldArrayProps.remove(index);
      },
    });
  };

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <form onSubmit={handleSave}>
          <SignersSectionHeader
            isSaving={isPostLoading}
            onAdd={handleAppend}
            customLinks={getLinks}
          />

          {!data || isGetLoading ? (
            <Loader />
          ) : (
            <LogbookSignersForm
              {...fieldArrayProps}
              remove={handleRemove}
              logbooks={data.logbooks}
              roles={data.roles}
            />
          )}
        </form>
      </FormProvider>
    </PageContainer>
  );
};

export default LogbookSigners;
